import React, {useId} from "react";
import {Box, Button, Checkbox, FormControlLabel, IconButton, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {Add, DeleteOutline} from "@mui/icons-material";
import {Controller} from "react-hook-form";
import {getSettingsColors} from "../SettingsDesign";

function ConversionSection({title, description, onAdd, addLabel, disabled, children}) {
    return (
        <Box component="section" sx={{minWidth: 0, p: {xs: 1.5, sm: 2}, borderRadius: "12px",
            bgcolor: theme => getSettingsColors(theme).inset}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5}}>
                <Typography component="h3" sx={{fontSize: 14, fontWeight: 600}}>{title}</Typography>
                {onAdd &&
                    <Button type="button" variant="outlined" size="small" startIcon={<Add/>} onClick={onAdd}
                            disabled={disabled} sx={{minHeight: 32, whiteSpace: "nowrap"}}>
                        {addLabel}
                    </Button>
                }
            </Box>
            {description &&
                <Typography sx={{fontSize: 13, color: "text.secondary", mb: 2, overflowWrap: "anywhere"}}>
                    {description}
                </Typography>
            }
            <Box sx={{display: "grid", gap: 1.5, minWidth: 0}}>{children}</Box>
        </Box>
    );
}

function ConversionEntry({title, removeLabel, onRemove, children}) {
    return (
        <Box sx={{minWidth: 0, p: 1.5, borderRadius: "10px", bgcolor: theme => getSettingsColors(theme).card}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5, minHeight: 28}}>
                <Typography component="h4" sx={{fontSize: 12, fontWeight: 600, color: "text.secondary"}}>{title}</Typography>
                {onRemove &&
                    <Tooltip title={removeLabel}>
                        <IconButton type="button" size="small" color="error" onClick={onRemove} aria-label={removeLabel}>
                            <DeleteOutline fontSize="small"/>
                        </IconButton>
                    </Tooltip>
                }
            </Box>
            {children}
        </Box>
    );
}

export default function IntegrationConversionSettings({
    provider,
    control,
    register,
    errors,
    enabled,
    eventFields,
    onAddEvent,
    onDeleteEvent,
    eventLimit = 25,
    eventLabel = "Conversion Event Name",
    eventPlaceholder = `Enter ${provider} conversion event name`,
    pixelFields,
    onAddPixel,
    onDeletePixel,
    initializePixels = false,
    children
}) {
    const toggleId = useId();

    return (
        <Box sx={{display: "grid", gap: 2, mt: 2, pb: 1, minWidth: 0,
            '& .MuiTextField-root': {m: 0, minWidth: 0},
            '&& .MuiOutlinedInput-root': {bgcolor: theme => getSettingsColors(theme).input},
            '& .MuiFormHelperText-root': {mx: 0, mt: 0.75, whiteSpace: "normal", overflowWrap: "anywhere"},
            '& .MuiFormControlLabel-root': {ml: -1, mr: 0},
            '& .MuiFormControlLabel-label': {fontSize: 13, whiteSpace: "normal"}
        }}>
            <Box sx={{display: "flex", alignItems: "flex-start", gap: 2, p: 2, borderRadius: "12px",
                bgcolor: theme => getSettingsColors(theme).inset}}>
                <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography component="label" htmlFor={toggleId} sx={{display: "block", fontSize: 15, fontWeight: 600}}>
                        Enable Conversion API
                    </Typography>
                    <Typography sx={{fontSize: 13, color: "text.secondary", mt: 0.75}}>
                        Send conversion events back to {provider} to improve reporting and campaign optimization.
                    </Typography>
                </Box>
                <Controller name="enableConversionAPI" control={control} defaultValue={false}
                            render={({field: {onChange, value, ref, ...field}}) => (
                                <Switch {...field} inputRef={ref} checked={!!value} color="primary"
                                        slotProps={{input: {id: toggleId}}}
                                        sx={{flexShrink: 0, mr: -1, mt: -0.5}}
                                        onChange={(event) => {
                                            const checked = event.target.checked;
                                            onChange(checked);
                                            if (checked && eventFields?.length === 0) {
                                                onAddEvent();
                                            }
                                            if (checked && initializePixels && pixelFields?.length === 0) {
                                                onAddPixel();
                                            }
                                        }}/>
                            )}/>
            </Box>
            {enabled &&
                <>
                    {children &&
                        <ConversionSection title="Manager account" description="Optionally send conversions to your Google manager account.">
                            {children}
                        </ConversionSection>
                    }
                    <ConversionSection title="Conversion events" description={`Match each event to the name or value configured in ${provider}.`}
                                       onAdd={onAddEvent} addLabel="Add event" disabled={eventFields?.length >= eventLimit}>
                        {eventFields?.map(({id, eventId, name, includePayout}, index) => {
                            register(`events.${index}.eventId`, {value: eventId});
                            return (
                                <ConversionEntry key={id} title={`Event ${index + 1}`} removeLabel={`Remove conversion event ${index + 1}`}
                                                 onRemove={eventFields.length > 1 ? () => onDeleteEvent({eventId, index}) : undefined}>
                                    <TextField variant="outlined" size="small" fullWidth label={eventLabel}
                                               placeholder={eventPlaceholder} defaultValue={name}
                                               error={!!errors?.events?.[index]?.name} helperText={errors?.events?.[index]?.name?.message}
                                               slotProps={{inputLabel: {shrink: true}}}
                                               {...register(`events.${index}.name`, {
                                                   required: {value: true, message: "This field is mandatory"},
                                                   maxLength: {value: 100, message: "Max length is 100 letters"}
                                               })}/>
                                    <Controller name={`events.${index}.includePayout`} control={control} defaultValue={includePayout || false}
                                                render={({field: {onChange, value, ref, ...field}}) => (
                                                    <FormControlLabel sx={{mt: 0.5}} label="Include conversion payout"
                                                                      control={<Checkbox {...field} inputRef={ref} color="primary" size="small"
                                                                                         checked={!!value} onChange={event => onChange(event.target.checked)}/>}/>
                                                )}/>
                                </ConversionEntry>
                            );
                        })}
                    </ConversionSection>
                    {pixelFields &&
                        <ConversionSection title="Pixels" description={`Connect the pixels used to send events to ${provider}.`}
                                           onAdd={onAddPixel} addLabel="Add pixel" disabled={pixelFields.length >= 20}>
                            {pixelFields.map(({id, rowId, name, pixelId, pixelAccessToken}, index) => {
                                register(`pixels.${index}.rowId`, {value: rowId});
                                return (
                                    <ConversionEntry key={id} title={`Pixel ${index + 1}`} removeLabel={`Remove pixel ${index + 1}`}
                                                     onRemove={pixelFields.length > 1 ? () => onDeletePixel({pixelId: rowId, index}) : undefined}>
                                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))"}, gap: 2}}>
                                            <TextField variant="outlined" size="small" fullWidth label="Pixel Name"
                                                       placeholder={`Enter ${provider} Pixel Name`} defaultValue={name}
                                                       error={!!errors?.pixels?.[index]?.name} helperText={errors?.pixels?.[index]?.name?.message}
                                                       slotProps={{inputLabel: {shrink: true}}}
                                                       {...register(`pixels.${index}.name`, {
                                                           required: {value: true, message: "This field is mandatory"},
                                                           maxLength: {value: 255, message: "Max length is 255 letters"}
                                                       })}/>
                                            <TextField variant="outlined" size="small" fullWidth label="Pixel ID"
                                                       placeholder={`Enter ${provider} Pixel ID`} defaultValue={pixelId}
                                                       error={!!errors?.pixels?.[index]?.pixelId} helperText={errors?.pixels?.[index]?.pixelId?.message}
                                                       slotProps={{inputLabel: {shrink: true}}}
                                                       {...register(`pixels.${index}.pixelId`, {
                                                           required: {value: true, message: "This field is mandatory"},
                                                           maxLength: {value: 50, message: "Max length is 50 letters"}
                                                       })}/>
                                            <TextField variant="outlined" size="small" fullWidth label="Conversion API Access Token"
                                                       placeholder="Enter Conversion API Access Token" defaultValue={pixelAccessToken}
                                                       sx={{gridColumn: "1 / -1"}}
                                                       error={!!errors?.pixels?.[index]?.pixelAccessToken} helperText={errors?.pixels?.[index]?.pixelAccessToken?.message}
                                                       slotProps={{inputLabel: {shrink: true}}}
                                                       {...register(`pixels.${index}.pixelAccessToken`, {
                                                           required: {value: true, message: "This field is mandatory"},
                                                           maxLength: {value: 1024, message: "Max length is 1024 letters"}
                                                       })}/>
                                        </Box>
                                    </ConversionEntry>
                                );
                            })}
                        </ConversionSection>
                    }
                </>
            }
        </Box>
    );
}
